import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Stack, Tab, Tabs } from "@mui/material";
import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import { SDK_DOCS_URL } from "config/constants";
import { ServiceWorkerContext, ServiceWorkerContextType } from "context/serviceWorkerContext";
import { useDetectorContext } from "hooks/useDetectorContext";
import { Typography } from "new-components/typography/Typography";
import { LEFT_TABS, RIGHT_TABS, TAB_TO_METADATA, TabChoices } from "routing/constants";
import { RouteSpec, tabToPath } from "routing/routeSpecs";
import { User } from "types/users";
import { tabIsAccessible } from "utils/accesibleTabsUtils";
import { redirectToDataPage } from "utils/redirectDataPage";


function getNavTab(choice: TabChoices, user: User | undefined, getLink: (choice: string) => string) {
  const metadata = TAB_TO_METADATA[choice];
  const badge = user ? metadata.getBadge?.(user) : null;

  return (
    <Tab
      component={Link}
      to={getLink(choice)}
      target={choice === TabChoices.Docs ? "_blank" : undefined}
      value={choice}
      label={
        <Stack
          direction="row"
          className={twMerge(metadata.opensInNewTab && "text-primary-darkest bg-white px-[10px] py-1 rounded-[5px]")}
        >
          {metadata.getLabel(user)}

          {metadata.opensInNewTab && <OpenInNewIcon className="ml-3 my-auto" />}

          {badge && (
            <Box className="bg-white px-[4.5px] py-[1.25px] rounded-[2.25px] h-fit flex normal-case ml-0.5">
              <Typography variant="small" fontWeight="bold" className="text-black">
                {badge}
              </Typography>
            </Box>
          )}
        </Stack>
      }
      key={choice}
      className="px-4 py-2"
      sx={{
        fontSize: "20px",
        color: "rgba(255, 255, 255, 0.75)",
        fontWeight: 300,
        "&.Mui-selected": { color: "white", fontWeight: 700 },
        "&:hover": { backgroundColor: (theme) => theme.palette.axon.subtle },
      }}
    />
  );
}

interface NavTabProps {
  routeSpec?: RouteSpec;
  user?: User;
}

export default function NavTabs(props: NavTabProps) {
  const { routeSpec, user } = props;
  const { currentDetectorId, setCurrentDetectorId } = useDetectorContext();

  const { isUpdateReady, updateServiceWorker } = useContext(ServiceWorkerContext) as ServiceWorkerContextType;

  function handleChangeTab(newTab: TabChoices) {
    const urlString = tabToPath(newTab);
    if (isUpdateReady) {
      updateServiceWorker(urlString);
    }
  }

  function handleClick(event: React.MouseEvent) {
    const target = event.target as Element;
    if (target.ariaSelected) {
      const selected: string = target.textContent || Object.values(TabChoices)[0];
      const typedKey = selected as keyof typeof TabChoices;
      if (TabChoices[typedKey] === TabChoices.Data && currentDetectorId) {
        setCurrentDetectorId(null);
        return;
      }
      setCurrentDetectorId(null);
      handleChangeTab(TabChoices[typedKey]);
    }
  }

  const getLink = (choice: string) => {
    if (currentDetectorId && choice === TabChoices.Data) return redirectToDataPage({ detectorId: currentDetectorId });
    if (choice === TabChoices.Docs) return SDK_DOCS_URL;
    if (Object.keys(TabChoices).includes(choice)) return tabToPath(TabChoices[choice as TabChoices]);
    return "";
  };

  // Get all accessible tabs
  const accessibleTabs = Object.keys(TabChoices).filter((choice) => tabIsAccessible(choice, user));

  // Ensure `value` matches an accessible tab or defaults to the first accessible tab
  const currentTab = routeSpec?.tab && accessibleTabs.includes(routeSpec.tab) ? routeSpec.tab : accessibleTabs[0]; // Default to the first accessible tab

  return (
    <Tabs
      value={currentTab}
      onClick={(event) => handleClick(event)}
      className="hidden sm:block h-[50px] w-full" // Hide on small screens
      sx={{
        "& .MuiTabs-indicator": {
          backgroundColor: (theme) => theme.palette.axon.main,
          height: "2px"
        }
      }}
    >
      {LEFT_TABS.filter((tab) => tabIsAccessible(tab, user)).map((tab) => getNavTab(tab, user, getLink))}
      <Box className="w-full" />
      {RIGHT_TABS.filter((tab) => tabIsAccessible(tab, user)).map((tab) => getNavTab(tab, user, getLink))}
    </Tabs>
  );
}
